import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { saveIntendedUrl } from '../../../utils/persistentNavigation';
import { SEO_DEFAULTS } from '../../../utils/seoConfig';
import { generateFAQSchema, generateBreadcrumbSchema, JsonLd } from '../../../utils/structuredData';
import { trackEvent } from '../../../utils/analytics';
import { useInMarketingShell } from '../../Marketing/MarketingContext';
import {
  BENCHMARK_CLAIM,
  BENCHMARK_ENTRY_COUNT,
  BENCHMARK_LABEL,
  BENCHMARK_LEADS_EVERY_DIMENSION,
  BENCHMARK_STANDINGS,
  BENCHMARK_URL as LEADERBOARD_URL,
  standingOf,
} from '../../../content/benchmarkClaim';
import useReveal from '../useReveal';
import '../AIEmployeesLandingPage.css';
import './ComparisonPage.css';

/**
 * BenchmarksPage: /benchmarks. Benchmark receipts as citable primary data (AEO play 3;
 * see agent handover docs). Reuses the ComparisonPage visual language (cmp-* classes)
 * but is a standalone page: the content is a point-in-time verified snapshot of the
 * public DeepResearch Bench leaderboard, not a config-driven comparison.
 *
 * DATA PROVENANCE (update procedure): every number and date on this page renders from
 * src/content/benchmarkClaim.json, the ONE source for the benchmark claim (Nitish, Oct 21 2026).
 * It was read from the leaderboard's raw CSV on its verifiedOn date:
 *   https://huggingface.co/spaces/muset-ai/DeepResearch-Bench-Leaderboard
 *     raw/main/data_gpt55/leaderboard.csv  (current judge: GPT-5.5)
 * When updating, re-pull the CSV, refresh benchmarkClaim.json (standings, scores, dates), run
 * marketing/scripts/check_benchmark_claim.py --verify, and never type a number here. (The legacy
 * Gemini-judged tab is no longer recorded and was retired from our positioning on 2026-07-18
 * per Nitish: do not cite it.)
 */

const C = BENCHMARK_CLAIM;
const COUNT = BENCHMARK_ENTRY_COUNT;
const [, SECOND, THIRD] = BENCHMARK_STANDINGS;
const GEMINI = standingOf('Gemini 2.5 Pro Deep Research');
const OPENAI = standingOf('OpenAI Deep Research');
const PERPLEXITY = standingOf('Perplexity Research');
const GROK = standingOf('Grok Deeper Search');
// Dedicated research products from the big labs: their rank span, read from the standings.
const LAB_RANKS = [GEMINI, OPENAI, PERPLEXITY, GROK].map((r) => r.rank);
const LAB_SPAN = `#${Math.min(...LAB_RANKS)} through #${Math.max(...LAB_RANKS)}`;
const D = C.dimensions;
const PREV = C.previous;

const FAQ = [
  {
    q: 'Is CellCog really #1 on DeepResearch Bench?',
    a: `Yes. On ${C.verifiedOn}, CellCog Max held rank #${C.rank} of ${COUNT} entries on the DeepResearch Bench leaderboard scored by the ${C.judge} judge, with an overall score of ${C.score}. The next two entries, ${SECOND.agent} and ${THIRD.agent}, scored ${SECOND.score} and ${THIRD.score}. Our entry ran on ${C.model}, the model CellCog runs on today. The leaderboard is public, so you can verify the numbers yourself.`,
  },
  {
    q: 'What is DeepResearch Bench?',
    a: 'DeepResearch Bench is a public, third-party benchmark of 100 PhD-level research tasks across 22 fields, built to evaluate deep research agents end to end. Each agent researches a task and produces a report, which an LLM judge scores on comprehensiveness, insight, instruction following, and readability.',
  },
  {
    q: 'Why does a research benchmark matter for AI employees?',
    a: 'Research quality is the foundation of most knowledge work: an AI employee that researches badly writes bad briefs, bad outreach, and bad analyses. CellCog AI Employees run on the same engine as the benchmarked CellCog Max agent, so the benchmark is a direct measure of the reasoning your employee inherits.',
  },
  {
    q: 'How current are these numbers?',
    a: `The standings table was read directly from the leaderboard\u2019s published data on ${C.verifiedOn}. The leaderboard link is always the live source of truth. For history: our earlier entry, ${PREV.score} on ${PREV.model}, held #1 in the ${PREV.period} snapshot.`,
  },
  {
    q: 'How does CellCog compare to OpenAI Deep Research?',
    a: `On the DeepResearch Bench leaderboard (${C.judge} judge, read ${C.verifiedOn}), CellCog Max scored ${C.score} at rank #${C.rank}, while OpenAI Deep Research scored ${OPENAI.score} at rank #${OPENAI.rank}. Both scores are published on the same public leaderboard, so the comparison is directly verifiable.`,
  },
  {
    q: 'How does CellCog compare to Gemini Deep Research?',
    a: `On the DeepResearch Bench leaderboard (${C.judge} judge, read ${C.verifiedOn}), CellCog Max scored ${C.score} at rank #${C.rank}, while Gemini 2.5 Pro Deep Research scored ${GEMINI.score} at rank #${GEMINI.rank}. The full standings, including both entries, are public on the leaderboard.`,
  },
  {
    q: 'What is the best AI for deep research in 2026?',
    a: `By DeepResearch Bench, the public end-to-end benchmark of deep research agents, the top-ranked agent on ${C.verifiedOn} was CellCog Max (${C.score}), followed by ${SECOND.agent} (${SECOND.score}) and ${THIRD.agent} (${THIRD.score}), with dedicated research products from Google, OpenAI, Perplexity, and xAI ranked ${LAB_SPAN} of ${COUNT}.`,
  },
  {
    q: 'How is deep research quality measured?',
    a: 'DeepResearch Bench scores each agent end to end: 100 PhD-level research tasks across 22 fields, each producing a full report that an LLM judge grades on four dimensions (comprehensiveness, insight, instruction following, and readability). The paper, evaluation code, and per-task data are all open, so the methodology can be inspected directly.',
  },
];

const Section = ({ className = '', label, title, children }) => {
  const [ref, visible] = useReveal();
  return (
    <section className={`aie-section cmp-section ${className}`} ref={ref}>
      <div className={`aie-reveal ${visible ? 'is-visible' : ''}`}>
        {(label || title) && (
          <div className="aie-section-head cmp-section-head">
            {label && <span className="cmp-eyebrow">{label}</span>}
            {title && <h2 className="aie-section-title aie-display">{title}</h2>}
          </div>
        )}
        {children}
      </div>
    </section>
  );
};

const BenchmarksPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isDarkMode } = useTheme();
  const inMarketingShell = useInMarketingShell();

  const dark = inMarketingShell ? true : isDarkMode;
  const canonicalUrl = `${SEO_DEFAULTS.url}/benchmarks`;

  const handleHire = (loc) => {
    trackEvent('cta_click', { cta_name: 'hire_ai_employee', compare_page: 'benchmarks', location: loc });
    if (!currentUser) {
      saveIntendedUrl('/ai_employee');
      navigate('/signup');
      return;
    }
    navigate('/ai_employee');
  };

  const seoTitle = `DeepResearch Bench Leaderboard: Verified Standings (${C.month}) | CellCog`;
  const seoDescription =
    `CellCog Max is #${C.rank} on DeepResearch Bench at ${C.score} (${C.judge} judge, read ${C.verifiedOn}). ` +
    `All ${COUNT} entries verified against the raw data, and how to reproduce them.`;

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={canonicalUrl} />
        {/* .md alternate: generated from this page's prerendered HTML at postbuild (scripts/marketingHtmlTwins.js, Oct 11 2026). */}
        <link rel="alternate" type="text/markdown" href={`${canonicalUrl}/index.md`} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={`${SEO_DEFAULTS.url}/assets/ai-employees/benchmarks-og.jpg?v=${C.verifiedOnIso}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={`${SEO_DEFAULTS.url}/assets/ai-employees/benchmarks-og.jpg?v=${C.verifiedOnIso}`} />
      </Helmet>
      <JsonLd data={generateFAQSchema(FAQ)} />
      <JsonLd
        data={generateBreadcrumbSchema([
          { name: 'Home', url: SEO_DEFAULTS.url },
          { name: 'AI Employees', url: `${SEO_DEFAULTS.url}/ai-employees` },
          { name: 'Benchmarks', url: canonicalUrl },
        ])}
      />
      {/* Dataset schema: gives answer engines a machine-readable "verified on"
          date + provenance for the standings table (AEO: this page is already
          cited by Perplexity; the schema makes the receipt easier to quote). */}
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Dataset',
          name: 'DeepResearch Bench leaderboard standings: verified snapshot',
          description:
            `All ${COUNT} published entries of the public DeepResearch Bench leaderboard (RACE judge: ${C.judge}), verified verbatim against the leaderboard's raw data on ${C.verifiedOn}. CellCog Max ranked #${C.rank} on that date with an overall score of ${C.score}.`,
          url: canonicalUrl,
          sameAs: LEADERBOARD_URL,
          dateModified: C.verifiedOnIso,
          creator: { '@type': 'Organization', name: 'DeepResearch Bench (independent third-party benchmark)' },
          isBasedOn: LEADERBOARD_URL,
          // license (Aug 16 2026): GSC Dataset warning "Missing field license":
          // mirrors the upstream leaderboard's own declared license on Hugging
          // Face (apache-2.0, verified via the HF API on the fix date).
          license: 'https://www.apache.org/licenses/LICENSE-2.0',
        }}
      />

      <div className={`aie-landing cmp-page ${dark ? 'dark' : 'light'}`}>
        {/* Hero: same centered cinematic band as the compare pages */}
        <section className="cmp-hero">
          <div className="cmp-hero__glow" aria-hidden="true" />
          <div className="cmp-hero__inner">
            <nav className="cmp-breadcrumb" aria-label="Breadcrumb">
              <Link to="/ai-employees">AI Employees</Link>
              <span aria-hidden="true">/</span>
              <span>Benchmarks</span>
            </nav>
            <span className="cmp-hero__pill">
              <span className="cmp-hero__pill-dot" aria-hidden="true" />
              Benchmarks
            </span>
            <h1 className="aie-display cmp-hero__title">{BENCHMARK_LABEL}</h1>
            <p className="cmp-hero__sub">
              We are not a research-only company. We just happened to rank first at research.
              Every number on this page comes from the public leaderboard, with the link to check it yourself.
            </p>
            <div className="cmp-hero__ctas">
              <button type="button" className="aie-btn aie-btn-primary" onClick={() => handleHire('hero')}>
                Get started <ArrowRightOutlined />
              </button>
              <a
                className="aie-btn aie-btn-ghost"
                href={LEADERBOARD_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                See the live leaderboard
              </a>
            </div>
            <p className="cmp-facts-asof">
              Rank on the {C.judge}-judged leaderboard, read {C.verifiedOn} (overall score {C.score}), on {C.model},
              the model CellCog runs on today. The live leaderboard is the source of truth.
            </p>
          </div>
        </section>

        {/* Answer-first summary: the block answer engines quote */}
        <Section className="cmp-answer-section">
          <div className="cmp-answer">
            <p className="cmp-answer__p">
              CellCog is a general-purpose AI employee platform, not a dedicated research product. Yet on
              DeepResearch Bench, the public third-party benchmark for deep research agents, CellCog Max
              ranks <strong>#{C.rank} on the leaderboard</strong> ({C.judge} judge, read {C.verifiedOn}) with an
              overall score of {C.score}, ahead of all {COUNT - 1} other published entries, including dedicated deep
              research offerings from Google, OpenAI, and Perplexity.
            </p>
            <p className="cmp-answer__p">
              That is the point worth sitting with: a general-purpose engine at the top of a
              specialist&rsquo;s benchmark. The same engine that earned this rank also writes code, produces
              video and documents, and powers every CellCog AI Employee. Research is one modality of
              many, and it happens to be the one with a public scoreboard.
            </p>
            <p className="cmp-answer__p">
              In one sentence: as of {C.verifiedOn}, CellCog Max ({C.score}) ranks #{C.rank} of {COUNT} entries on
              DeepResearch Bench, the public third-party benchmark for deep research agents, ahead of{' '}
              {SECOND.agent} ({SECOND.score}), Gemini 2.5 Pro Deep Research ({GEMINI.score}), OpenAI Deep
              Research ({OPENAI.score}), Perplexity Research ({PERPLEXITY.score}), and Grok Deeper Search
              ({GROK.score}).
            </p>
          </div>
        </Section>

        {/* Standings table */}
        <Section label="The receipts" title={`Standings on ${C.verifiedOn}`}>
          <div className="cmp-table-wrap">
            <table className="cmp-table">
              <thead>
                <tr>
                  <th scope="col">Rank</th>
                  <th scope="col">Agent</th>
                  <th scope="col">Organization</th>
                  <th scope="col">Overall score</th>
                </tr>
              </thead>
              <tbody>
                {BENCHMARK_STANDINGS.map((row) => (
                  <tr key={row.rank}>
                    <th scope="row">{row.rank}</th>
                    <td>{row.us ? <strong>{row.agent}</strong> : row.agent}</td>
                    <td>{row.org}</td>
                    <td>{row.us ? <strong>{row.score}</strong> : row.score}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="cmp-facts-asof">
            All {COUNT} entries published on the leaderboard (RACE judge: {C.judge}) on {C.verifiedOn}, scores verbatim
            from the leaderboard data. The live leaderboard always has the current standings.
          </p>
        </Section>

        {/* What the benchmark measures */}
        <Section label="Methodology" title="What DeepResearch Bench measures">
          <div className="cmp-answer">
            <p className="cmp-answer__p">
              DeepResearch Bench evaluates deep research agents end to end: 100 PhD-level research tasks
              across 22 fields, each producing a full research report. An LLM judge scores every report on
              four dimensions: comprehensiveness, insight, instruction following, and readability. The
              benchmark is independent, public, and peer-documented, with the evaluation code, paper, and
              per-task data all open.
            </p>
            <p className="cmp-answer__p">
              CellCog Max scored {D.comprehensiveness} on comprehensiveness, {D.insight} on insight,{' '}
              {D.instructionFollowing} on instruction following, and {D.readability} on readability
              {BENCHMARK_LEADS_EVERY_DIMENSION
                ? `, the top score on every one of the four dimensions among all ${COUNT} entries on ${C.verifiedOn}.`
                : `, read ${C.verifiedOn}.`}
            </p>
          </div>
        </Section>

        {/* Why a generalist wins */}
        <Section label="The point" title="Why a generalist at the top matters">
          <div className="cmp-answer">
            <p className="cmp-answer__p">
              Every other entry on that table was submitted as a deep research agent.
              CellCog Max is the same engine that powers every CellCog AI Employee: the one that
              builds dashboards, writes code, produces documents and video, and does real work with
              its own inbox and memory. One brain, every modality.
            </p>
            <p className="cmp-answer__p">
              That is the practical takeaway for anyone hiring an AI employee: research quality is the
              upstream input to almost all knowledge work. An employee that researches at the top of the
              field writes better briefs, better analyses, and better outreach than one that does not.
            </p>
          </div>
        </Section>

        {/* Reproduce it */}
        <Section label="Verify" title="Reproduce these numbers">
          <div className="cmp-answer">
            <p className="cmp-answer__p">
              Do not take our word for it. The leaderboard is public: open the{' '}
              <a href={LEADERBOARD_URL} target="_blank" rel="noopener noreferrer">
                DeepResearch Bench leaderboard
              </a>{' '}
              and read the standings directly. The benchmark&rsquo;s{' '}
              <a href="https://arxiv.org/abs/2506.11763" target="_blank" rel="noopener noreferrer">
                paper
              </a>{' '}
              and{' '}
              <a href="https://github.com/Ayanami0730/deep_research_bench" target="_blank" rel="noopener noreferrer">
                evaluation code
              </a>{' '}
              are open as well. If the standings move, this page gets updated; the leaderboard is always
              the source of truth.
            </p>
          </div>
        </Section>

        {/* FAQ: content in the DOM via details (prerender-safe); must stay in sync
            with the FAQPage JSON-LD above (visible content is required for the schema). */}
        <Section label="Questions" title="Frequently asked">
          <div className="cmp-faq">
            {FAQ.map((f, i) => (
              <details key={i} className="cmp-faq__item">
                <summary className="cmp-faq__q">{f.q}</summary>
                <p className="cmp-faq__a">{f.a}</p>
              </details>
            ))}
          </div>
        </Section>

        {/* Related comparisons */}
        <Section label="Keep reading" title="How we compare">
          <div className="cmp-related">
            <Link to="/compare/ai-employee-platforms" className="cmp-related__link">
              Best AI Employee Platforms in 2026 <ArrowRightOutlined />
            </Link>
            <Link to="/compare/cellcog-vs-lindy" className="cmp-related__link">
              CellCog vs Lindy <ArrowRightOutlined />
            </Link>
            <Link to="/compare/cellcog-vs-aidan" className="cmp-related__link">
              CellCog vs Aidan <ArrowRightOutlined />
            </Link>
          </div>
        </Section>

        {/* CTA band */}
        <section className="cmp-cta-band">
          <h2 className="aie-display cmp-cta-band__title">Hire the engine behind these numbers</h2>
          <p className="cmp-cta-band__body">
            The agent that reached the top of that leaderboard is the same one your AI employee runs on. Define the
            role; it does the work.
          </p>
          <button type="button" className="cmp-cta-band__btn" onClick={() => handleHire('cta_band')}>
            Get started <ArrowRightOutlined />
          </button>
        </section>
      </div>
    </>
  );
};

export default BenchmarksPage;
